import { useRef, useState } from 'react';
import { compressImage, formatFileSize, isCompressibleImage } from '../lib/imageCompression';
import {
  useCreatePhotoCategory,
  usePhotoCategories,
  useUploadPhoto,
  type PhotoCategory,
} from '../lib/photos';
import { apiErrorMessage } from '../lib/api';
import { Modal } from './Modal';

interface PhotoUploadButtonProps {
  projectId: number;
  /** Vorausgewaehlte Kategorie. Falls null: User waehlt im Modal. */
  presetCategoryId?: number | null;
  onUploaded?: () => void;
  label?: string;
  /** Wenn true: nutzt die System-Kamera direkt (Mobile). */
  cameraOnly?: boolean;
}

interface UploadProgress {
  total: number;
  done: number;
  current: string | null;
  errors: string[];
}

/**
 * Two-Step Foto-Upload:
 *   1. Click → File-Picker (oder Kamera)
 *   2. Datei(en) gewaehlt → Modal mit Kategorie-Auswahl + Caption
 *   3. Bestaetigen → komprimieren + hochladen
 *
 * So muss der User vor jedem Upload aktiv die Kategorie waehlen
 * (oder explizit "ohne Kategorie") — kein versehentliches Ablegen
 * im falschen Topf.
 */
export function PhotoUploadButton({
  projectId,
  presetCategoryId = null,
  onUploaded,
  label = 'Foto hochladen',
  cameraOnly = false,
}: PhotoUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const categoriesQ = usePhotoCategories(projectId);
  const createCategory = useCreatePhotoCategory(projectId);
  const upload = useUploadPhoto(projectId);

  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null | 'PRESET'>('PRESET');
  const [caption, setCaption] = useState('');
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const handlePickClick = () => {
    inputRef.current?.click();
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setPendingFiles(Array.from(files));
    // Wenn eine konkrete Kategorie vorausgewaehlt ist, diese vorbelegen.
    // Sonst: User muss aktiv waehlen (Default 'null' = ohne Kategorie).
    setSelectedCategoryId(presetCategoryId != null ? 'PRESET' : null);
    setCaption('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const closeModal = () => {
    if (progress?.current) return; // nicht schliessbar waehrend Upload
    setPendingFiles(null);
    setProgress(null);
  };

  const handleCreateCategory = async () => {
    const name = window.prompt('Name der neuen Kategorie:\n(z.B. "Einlagen Decke UG")');
    if (!name) return;
    try {
      const created = await createCategory.mutateAsync(name);
      setSelectedCategoryId(created.id);
    } catch (err) {
      alert(apiErrorMessage(err, 'Kategorie konnte nicht angelegt werden'));
    }
  };

  const effectiveCategoryId: number | null =
    selectedCategoryId === 'PRESET' ? presetCategoryId : selectedCategoryId;

  const handleStartUpload = async () => {
    if (!pendingFiles) return;
    setProgress({ total: pendingFiles.length, done: 0, current: null, errors: [] });

    for (const file of pendingFiles) {
      setProgress((s) => (s ? { ...s, current: file.name } : null));
      try {
        let blob: Blob = file;
        let width: number | undefined;
        let height: number | undefined;
        let uploadedName = file.name;

        if (isCompressibleImage(file)) {
          try {
            const compressed = await compressImage(file);
            blob = compressed.blob;
            width = compressed.width;
            height = compressed.height;
            const base = file.name.replace(/\.[^./\\]+$/, '');
            uploadedName = `${base}.jpg`;
          } catch (compErr) {
            console.warn('Komprimierung fehlgeschlagen, lade Original:', compErr);
          }
        }

        await upload.mutateAsync({
          blob,
          originalName: uploadedName,
          categoryId: effectiveCategoryId,
          caption: caption.trim() || null,
          width,
          height,
        });
        setProgress((s) => (s ? { ...s, done: s.done + 1 } : null));
      } catch (err) {
        const msg = apiErrorMessage(err, `Upload fehlgeschlagen: ${file.name}`);
        setProgress((s) => (s ? { ...s, done: s.done + 1, errors: [...s.errors, msg] } : null));
      }
    }

    setProgress((s) => (s ? { ...s, current: null } : null));
    onUploaded?.();

    // Bei Erfolg Modal schliessen, bei Fehlern offen lassen
    setTimeout(() => {
      setProgress((current) => {
        if (current && current.errors.length === 0) {
          setPendingFiles(null);
          return null;
        }
        return current;
      });
    }, 1200);
  };

  const isUploading = progress != null && progress.current != null;
  const isDone = progress != null && progress.current == null;
  const categories: PhotoCategory[] = categoriesQ.data ?? [];

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture={cameraOnly ? 'environment' : undefined}
        multiple={!cameraOnly}
        onChange={(e) => handleFiles(e.target.files)}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={handlePickClick}
      >
        {label}
      </button>

      <Modal
        open={pendingFiles != null}
        title={
          pendingFiles
            ? pendingFiles.length === 1
              ? '1 Foto hochladen'
              : `${pendingFiles.length} Fotos hochladen`
            : ''
        }
        onClose={closeModal}
        width={520}
        footer={
          <>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={closeModal}
              disabled={!!isUploading}
            >
              {isDone ? 'Schliessen' : 'Abbrechen'}
            </button>
            {!isDone && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStartUpload}
                disabled={isUploading}
              >
                {isUploading
                  ? `Lade ${progress.done + 1}/${progress.total} …`
                  : 'Hochladen'}
              </button>
            )}
          </>
        }
      >
        {pendingFiles && (
          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="form-group full">
              <label className="form-label">Kategorie *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={
                    selectedCategoryId === 'PRESET'
                      ? `preset:${presetCategoryId ?? ''}`
                      : selectedCategoryId === null
                        ? 'null'
                        : String(selectedCategoryId)
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === 'NEW') {
                      void handleCreateCategory();
                      return;
                    }
                    if (v === 'null') setSelectedCategoryId(null);
                    else if (v.startsWith('preset:')) setSelectedCategoryId('PRESET');
                    else setSelectedCategoryId(Number(v));
                  }}
                  disabled={!!isUploading || isDone}
                  style={{ flex: 1 }}
                >
                  {presetCategoryId != null && (
                    <option value={`preset:${presetCategoryId}`}>
                      ★ {categories.find((c) => c.id === presetCategoryId)?.name ?? 'Aktuelle Kategorie'}
                    </option>
                  )}
                  <option value="null">— Ohne Kategorie —</option>
                  {categories
                    .filter((c) => c.id !== presetCategoryId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  <option value="NEW">+ Neue Kategorie anlegen…</option>
                </select>
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-soft)', marginTop: 6 }}>
                Wo gehören diese Fotos hin? Du kannst sie später auch verschieben.
              </div>
            </div>

            <div className="form-group full">
              <label className="form-label">Beschriftung (optional)</label>
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="z.B. Rohbau Süd, vor Verputz"
                disabled={!!isUploading || isDone}
                maxLength={500}
              />
              {pendingFiles.length > 1 && caption.trim() && (
                <div style={{ fontSize: 11, color: 'var(--color-text-soft)', marginTop: 4 }}>
                  Beschriftung wird auf alle {pendingFiles.length} Fotos angewendet.
                </div>
              )}
            </div>

            <div className="form-group full">
              <label className="form-label">Auswahl</label>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--color-text-soft)',
                  background: 'var(--color-surface-alt)',
                  padding: 8,
                  borderRadius: 6,
                  maxHeight: 120,
                  overflowY: 'auto',
                }}
              >
                {pendingFiles.map((f, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {progress && progress.errors[i - progress.done]
                        ? '⚠ '
                        : progress && i < progress.done
                          ? '✓ '
                          : ''}
                      {f.name}
                    </span>
                    <span style={{ flexShrink: 0, color: 'var(--color-text-muted)' }}>
                      {formatFileSize(f.size)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {progress && progress.errors.length > 0 && (
              <div
                style={{
                  background: 'var(--color-danger-bg)',
                  color: 'var(--color-danger-text)',
                  padding: 10,
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                {progress.errors.map((e, i) => (
                  <div key={i}>⚠ {e}</div>
                ))}
              </div>
            )}

            {isDone && progress && progress.errors.length === 0 && (
              <div
                style={{
                  background: 'var(--color-success-bg, #e7f6ec)',
                  color: 'var(--color-success-text, #166534)',
                  padding: 10,
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                ✓ Alle {progress.total} Foto(s) erfolgreich hochgeladen.
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
